import React, { useEffect, useCallback } from 'react';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children }) => {
    // Close modal on 'Escape' key press
    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            onClose();
        }
    }, [onClose]);

    // Close modal on click outside of the content
    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    // Add/remove event listener based on isOpen
    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, handleKeyDown]);

    // Render nothing if modal is closed
    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
            onClick={handleOverlayClick}
        >
            <div className="bg-white rounded-lg shadow-lg w-11/12 max-w-2xl md:max-w-full max-h-[80vh] p-2 md:p-6 mx-3 md:mx-4 relative animate-fade-in scroll-y-auto overflow-y-auto">
                <button
                    onClick={onClose}
                    className="absolute top-2 right-2 text-gray-500 hover:text-red-600 text-2xl font-bold focus:outline-none"
                    aria-label="Close modal"
                >
                    &times;
                </button>
                {title && <h2 className="text-xl font-semibold mb-4 text-center">{title}</h2>}
                {children}
            </div>
        </div>
    );
};

export default Modal;
